import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './components/auth/AuthComponents';
import { UsageProvider } from './contexts/UsageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Navigation from './components/shared/Navigation';
import DashboardLayout from './components/layout/DashboardLayout';
import ModernHomepage from './components/homepage/ModernHomepage';
import AboutPage from './components/homepage/AboutPage';
import PricingPage from './components/homepage/PricingPage';
import DocumentAnalyzer from './components/contract/DocumentAnalyzer';
import Dashboard from './components/dashboard/Dashboard';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import HiringShortlistPage from './pages/HiringShortlistPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useOutletContext } from 'react-router-dom';
import PaymentResultPage from './pages/PaymentResultPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AuthPage from './pages/AuthPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import useIsMobile from './hooks/useIsMobile';
import MobilePricingPage from './components/homepage/MobilePricingPage';
import MobileMerderHomePage from './components/homepage/MobileMerderHomePage';
import RecentActivityPage from './pages/RecentActivityPage';
import MobileAboutPage from './components/homepage/MobileAboutPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ContactSupportForm from './components/shared/ContactSupportForm';
import Footer from './components/shared/Footer';
import RevampResultPage from './pages/RevampResultPage';
import RevampMenuPage from './pages/RevampMenuPage';
import GenerateDocument from './pages/GenerateDocument';
import GeneratedDocumentsPage from './pages/GeneratedDocumentsPage';
import JobBoardPage from './pages/JobBoardPage';
import SOPOpportunitiesPage from './pages/SOPOpportunitiesPage';
import FindALawyerPage from './pages/FindALawyerPage';
import LawyerPublicProfilePage from './pages/LawyerPublicProfilePage';
import LawyerApplyPage from './pages/LawyerApplyPage';
import MessagesPage from './pages/MessagesPage';
import EsignDashboardPage from './pages/EsignDashboardPage';
import EsignWizardPage from './pages/EsignWizardPage';
import SignPage from './pages/SignPage';

// Layout for public pages
const PublicLayout = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navigation showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />
      <div className="flex-1 flex flex-col min-h-0">
        <Outlet context={{ showAuthModal, setShowAuthModal, isMobile, onOpenContactModal: () => setShowContactModal(true) }} />
      </div>
      <Footer onOpenContactModal={() => setShowContactModal(true)} />
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'var(--color-overlay)' }}>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-2xl p-6 max-w-lg w-full relative">
            <button
              className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              onClick={() => setShowContactModal(false)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="text-xl font-bold text-[var(--color-text)] mb-4">Contact Support</h2>
            <ContactSupportForm
              initialName={user?.full_name || ''}
              initialEmail={user?.email || ''}
              onClose={() => setShowContactModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ThemedToastContainer = () => {
  const { theme } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme}
    />
  );
};

// ProtectedRoute wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Layout for protected pages
const DashboardLayoutWrapper = () => (
  <DashboardLayout>
    <Outlet />
  </DashboardLayout>
);

function HomePageSelector() {
  const { isMobile, setShowAuthModal } = useOutletContext();
  if (isMobile) return <MobileMerderHomePage setShowAuthModal={setShowAuthModal} />;
  return <ModernHomepage />;
}

function AboutPageSelector() {
  const { isMobile, setShowAuthModal } = useOutletContext();
  if (isMobile) return <MobileAboutPage setShowAuthModal={setShowAuthModal} />;
  return <AboutPage />;
}
function PricingPageSelector() {
  const { isMobile, setShowAuthModal } = useOutletContext();
  if (isMobile) return <MobilePricingPage setShowAuthModal={setShowAuthModal} />;
  return <PricingPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UsageProvider>
          <HelmetProvider>
            <Router>
              <Routes>
                {/* Public Routes with PublicLayout */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<HomePageSelector />} />
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/about" element={<AboutPageSelector />} />
                  <Route path="/pricing" element={<PricingPageSelector />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/payment-result" element={<PaymentResultPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/lawyers/:slug" element={<LawyerPublicProfilePage />} />
                  <Route path="/sign/:token" element={<SignPage />} />
                </Route>
                {/* Auth routes WITHOUT navigation/header */}
                <Route path="/login" element={<AuthPage />} />
                <Route path="/signup" element={<AuthPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* Protected Routes with DashboardLayout */}
                <Route element={<ProtectedRoute><DashboardLayoutWrapper /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/document-analysis" element={<DocumentAnalyzer />} />
                  <Route path="/recent-activities" element={<RecentActivityPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/subscriptions" element={<SubscriptionPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/revamp-result/:id" element={<RevampResultPage />} />
                  <Route path="/revamp-document" element={<RevampMenuPage />} />
                  <Route path="/hiring-shortlist" element={<HiringShortlistPage />} />
                  <Route path="/generate-document" element={<GenerateDocument />} />
                  <Route path="/generated-documents" element={<GeneratedDocumentsPage />} />
                  <Route path="/jobs" element={<JobBoardPage />} />
                  <Route path="/my-job-match" element={<JobBoardPage />} />
                  <Route path="/sop-opportunities" element={<SOPOpportunitiesPage />} />
                  <Route path="/esign" element={<EsignDashboardPage />} />
                  <Route path="/esign/new" element={<EsignWizardPage />} />
                  <Route path="/esign/:id" element={<EsignWizardPage />} />
                  <Route path="/find-a-lawyer" element={<FindALawyerPage />} />
                  <Route path="/become-a-lawyer" element={<LawyerApplyPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/messages/:conversationId" element={<MessagesPage />} />
                </Route>
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
              <ThemedToastContainer />
            </Router>
          </HelmetProvider>
        </UsageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;